// src/components/ForYouPage.tsx
import { Paper, Text, Button, Stack, Center, Group } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconSparkles, IconPlus } from '@tabler/icons-react';
import { useWidgetPreferences } from '../hooks/useWidgetPreferences';
import { getWidgetById } from '../widgets/registry';
import WidgetGallery from '../widgets/WidgetGallery';
import type { Bill } from '../types';

interface ForYouPageProps {
	bills: Bill[];
	pastDueBills: Bill[];
	onUpdateBill: (bill: Bill) => void;
}

function ForYouPage({ bills, pastDueBills, onUpdateBill }: ForYouPageProps) {
	const [galleryOpened, { open: openGallery, close: closeGallery }] =
		useDisclosure(false);
	const { enabledWidgets, toggleWidget } = useWidgetPreferences();

	const hasWidgets = enabledWidgets.length > 0;

	return (
		<>
			<Stack gap='md'>
				{/* Header with add widget button when widgets exist */}
				{hasWidgets && (
					<Group justify='space-between' align='center'>
						<Group gap='xs'>
							<IconSparkles
								size={20}
								color='var(--mantine-color-violet-6)'
							/>
							<Text fw={600}>Your Dashboard</Text>
						</Group>
						<Button
							variant='light'
							size='xs'
							leftSection={<IconPlus size={14} />}
							onClick={openGallery}
						>
							Add Widget
						</Button>
					</Group>
				)}

				{/* Empty state */}
				{!hasWidgets && (
					<Paper withBorder p='xl' radius='md'>
						<Center>
							<Stack align='center' gap='md'>
								<IconSparkles
									size={48}
									color='var(--mantine-color-violet-6)'
									stroke={1.5}
								/>
								<Text fw={600} size='lg' ta='center'>
									Welcome to For You
								</Text>
								<Text
									size='sm'
									c='dimmed'
									ta='center'
									maw={300}
								>
									Personalize your dashboard by adding widgets
									that matter to you.
								</Text>
								<Button
									variant='filled'
									color='violet'
									leftSection={<IconPlus size={16} />}
									onClick={openGallery}
								>
									Get Widgets
								</Button>
							</Stack>
						</Center>
					</Paper>
				)}

				{/* Render enabled widgets */}
				{enabledWidgets.map((widgetId) => {
					const widget = getWidgetById(widgetId);
					if (!widget) return null;

					const WidgetComponent = widget.component;
					return (
						<WidgetComponent
							key={widgetId}
							bills={bills}
							pastDueBills={pastDueBills}
							onUpdateBill={onUpdateBill}
						/>
					);
				})}
			</Stack>

			<WidgetGallery
				opened={galleryOpened}
				onClose={closeGallery}
				enabledWidgets={enabledWidgets}
				onToggleWidget={toggleWidget}
			/>
		</>
	);
}

export default ForYouPage;
